import { Constant, ValueSymbol } from '@/modules/core/legacy/charts/am5/charts.constants';
import { ListColumn } from '@/modules/core/list/models/ListColumn';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import NumeralService from '@/modules/core/app/services/NumeralService';
import moment from 'moment';
import {
  Container,
  Triangle,
  Label,
  p50,
  color,
  Bullet,
  RoundedRectangle,
  p100,
} from '@amcharts/amcharts5';
import {
  PlotType,
  LineChartFullStackedLocationX,
  LineChartLocationX,
} from '@/modules/ta/legacy/widget/widget.constants';
import { useToolTips } from '@/modules/core/legacy/charts/am5/pie/composables/useTooltips';
import { useBullets } from '@/modules/core/legacy/charts/am5/line/composables/useBullets';

export function useComparison(context) {
  const { chart, root, config, isDarkTheme } = context();
  const { assignThemeColor } = useToolTips(context);

  function calculateDeltaValue(value, comparisonValue, column) {
    if (!value || !comparisonValue) {
      return 0;
    }
    const columnFormatCheck = new ListColumn(column);

    if (columnFormatCheck.isFormatNumerical()) {
      return calculateNumericalDelta(value, comparisonValue, column);
    }
    if (columnFormatCheck.isFormatTime()) {
      return calculateTimeDelta(value, comparisonValue, column);
    }
    if (columnFormatCheck.isFormatDateTime()) {
      return calculateDateTimeDelta(value, comparisonValue, column);
    }
    return 0;
  }

  /**
   * @param value
   * @param comparisonValue
   * @param column
   */
  function calculateNumericalDelta(value, comparisonValue, column) {
    const deltaValue = NumeralService.calculateDelta(
      value,
      comparisonValue,
      column.compare_as_value
    );
    return NumeralService.formatValue(Math.abs(deltaValue), ColumnFormat.FORMAT_INTEGER, 0);
  }

  /**
   * @param value
   * @param comparisonValue
   * @param column
   * @private
   */
  function calculateTimeDelta(value, comparisonValue, column) {
    // get seconds of each time (HH:mm:ss) as the unit of measure since seconds is the smallest measure
    if (value.toString().includes(':')) {
      value = value.split(':').reduce((acc, time) => 60 * acc + +time);
    }
    if (comparisonValue.toString().includes(':')) {
      comparisonValue = comparisonValue.split(':').reduce((acc, time) => 60 * acc + +time);
    }

    const deltaValue = NumeralService.calculateDelta(
      value,
      comparisonValue,
      column.compare_as_value
    );
    return NumeralService.formatValue(Math.abs(deltaValue), ColumnFormat.FORMAT_INTEGER, 0);
  }

  /**
   * @param value
   * @param comparisonValue
   * @param column
   */
  function calculateDateTimeDelta(value, comparisonValue, column) {
    const momentValue = moment(value).unix() / 1000;
    const momentComparisonValue = moment(comparisonValue).unix() / 1000;

    const deltaValue = NumeralService.calculateDelta(
      momentValue,
      momentComparisonValue,
      column.compare_as_value
    );
    return NumeralService.formatValue(Math.abs(deltaValue), ColumnFormat.FORMAT_INTEGER, 0);
  }

  /**
   * @param percentArray
   */
  function createBulletsForComparison(percentArray = []) {
    const { series } = chart.value;
    const dataLength = config.value.data?.length;
    let locationX = getLocationX(dataLength);
    if (percentArray.length) {
      series.each((itemSeries, index) => {
        let i = 0;
        if (percentArray[index]) {
          const lastIndex = series.length - 1;
          const item = itemSeries.dataItems[itemSeries.dataItems.length - 1];
          const { dataContext } = item;

          itemSeries.events.on(Constant.DATA_VALIDATED, () => {
            const { percentValue, status, statusLocation, tooltip, toggleArrowDirection } =
              percentArray[index];
            if (dataContext) {
              const container = createContainer(
                percentValue,
                itemSeries.get(Constant.FILL),
                status,
                tooltip,
                toggleArrowDirection
              );
              const locationValue = statusLocation ? 0.6 + (index / lastIndex) * 0.6 : locationX;
              itemSeries.bullets.push(() => {
                if (dataLength - 1 === i++) {
                  // doing this check in order overlapping of labels, so checking data length and applying for final value alone
                  return Bullet.new(root.value, {
                    locationX: locationValue,
                    sprite: container,
                  });
                }
              });
            }
          });
        }
      });
      // setting chart zoom level if more no. of datas are present
      const xAxes = chart.value.xAxes.getIndex(0);
      xAxes.zoom(0, 1.04);
    }
  }

  function createContainer(percentValue, fill, status, tooltipText, toggleArrowDirection) {
    const bullet = Container.new(root.value, {
      layout: root.horizontalLayout,
      cornerRadius: 15,
      background: createBackground(fill, tooltipText),
    });

    bullet.children.push(createLabel(percentValue));
    bullet.children.push(createTriangle(status, toggleArrowDirection));

    return bullet;
  }

  function createBackground(fill, tooltipText) {
    return RoundedRectangle.new(root.value, {
      fill,
      width: 50,
      height: 60,
      opacity: 0.3,
      cornerRadius: 15,
      interactive: true,
      showTooltipOn: Constant.HOVER,
      tooltipText,
      tooltipY: 78,
      dx: 3,
      dy: 0,
    });
  }

  function createLabel(percentValue) {
    const { plotType } = config.value;
    const fontColorPicker = assignThemeColor(isDarkTheme, config.value);
    const labelAxisPosition = percentValue.toString().length >= 3 ? -5 : -3;
    const { lineChartTemplate } = useBullets(context);
    // apply pts for 100% full stacked v2 and also remove % from N/A
    const formattedPercentValue =
      percentValue === Constant.NOT_APPLICABLE
        ? percentValue
        : `${percentValue}${
            plotType === PlotType.FULL_STACKED_V2
              ? ` ${ValueSymbol.LINE_CHART_STACKED_PTS}`
              : ValueSymbol[ColumnFormat.FORMAT_PERCENT]
          }`;

    return Label.new(
      root.value,
      {
        text: formattedPercentValue,
        centerX: p50,
        fontSize: 11,
        marginBottom: 500,
        fill: color(fontColorPicker),
        dx: plotType === PlotType.FULL_STACKED_V2 ? -20 : labelAxisPosition,
        dy: -11,
      },
      lineChartTemplate
    );
  }

  function createTriangle(status, toggleArrowDirection) {
    return Triangle.new(root.value, {
      width: 8,
      height: 5,
      fill: status ? color(0xff0000) : color(0x00cc00),
      rotation: toggleArrowDirection ? 360 : 180,
      centerY: p100,
      dy: toggleArrowDirection ? 5 : 0,
      dx: 14,
    });
  }

  function getLocationX(dataLength) {
    const { plotType } = config.value;
    let dxAxis = [];
    let defaultLocationForLargeData = 9;
    if (plotType === PlotType.FULL_STACKED_V2) {
      dxAxis = LineChartFullStackedLocationX;
      defaultLocationForLargeData = 15;
    } else {
      dxAxis = LineChartLocationX;
    }

    for (let i = 0; i < dxAxis.length; i++) {
      const [threshold, value] = dxAxis[i];
      if (dataLength < threshold) return value;
    }
    return defaultLocationForLargeData;
  }

  return {
    calculateDeltaValue,
    calculateNumericalDelta,
    calculateTimeDelta,
    calculateDateTimeDelta,
    createBulletsForComparison,
    getLocationX,
  };
}
